import type { LucideIcon } from 'lucide-react';

type AccentColor = 'default' | 'green' | 'red' | 'blue';

interface CompactStatProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: LucideIcon;
  iconAccent?: AccentColor;
  onClick?: () => void;
}

const ACCENT_CLASSES: Record<AccentColor, string> = {
  default: 'bg-muted text-muted-foreground',
  green: 'bg-green-500/10 text-green-500 dark:bg-green-900/20 dark:text-green-400',
  red: 'bg-red-500/10 text-red-500 dark:bg-red-900/20 dark:text-red-400',
  blue: 'bg-blue-500/10 text-blue-500 dark:bg-blue-900/20 dark:text-blue-400',
};

export function CompactStat({
  title,
  value,
  subtitle,
  icon: Icon,
  iconAccent = 'default',
  onClick,
}: CompactStatProps) {
  const clickable = typeof onClick === 'function';
  const interactiveProps = clickable
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        },
      }
    : {};

  return (
    <div
      className={[
        'flex items-center gap-2.5 rounded-md border border-border bg-card px-3 py-2.5',
        clickable
          ? 'cursor-pointer transition-colors hover:border-primary/50 hover:bg-accent/30'
          : '',
      ].join(' ')}
      {...interactiveProps}
    >
      {Icon && (
        <div
          className={[
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
            ACCENT_CLASSES[iconAccent],
          ].join(' ')}
        >
          <Icon size={14} />
        </div>
      )}
      <div className="flex min-w-0 flex-col">
        <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          {title}
        </span>
        <div className="flex items-baseline gap-1.5">
          <span className="text-lg font-bold leading-tight tabular-nums text-foreground">
            {value}
          </span>
          {subtitle && (
            <span className="text-[11px] tabular-nums text-muted-foreground">
              {subtitle}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
